import PropTypes from 'prop-types';
import { createContext, useCallback, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

// hooks
import useAuth from '../hooks/useAuth';
import { getRestaurants } from '../services/api/restaurants';

// ----------------------------------------------------------------------

const RestaurantContext = createContext();
const EMPTY_RESTAURANTS = [];

function RestaurantProvider({ children }) {
  const [restaurant, setRestaurant] = useState();
  const { isAuthenticated, user } = useAuth();

  const canReadRestaurants = isAuthenticated && Boolean(user?.permissions?.includes('restaurant:read'));
  const [selectionScope, setSelectionScope] = useState({ userId: user?.id, canReadRestaurants });
  if (selectionScope.userId !== user?.id || selectionScope.canReadRestaurants !== canReadRestaurants) {
    // Reset before rendering consumers so they cannot request the previous account's restaurant.
    setSelectionScope({ userId: user?.id, canReadRestaurants });
    setRestaurant(undefined);
  }
  const { data: restaurants = EMPTY_RESTAURANTS, isLoading } = useQuery({
    queryKey: ['restaurants', user?.id],
    queryFn: getRestaurants,
    enabled: canReadRestaurants,
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    cacheTime: 30 * 60 * 1000, // Keep data in cache for 30 minutes
  });

  const restaurantList = canReadRestaurants ? restaurants : EMPTY_RESTAURANTS;

  const onChangeRestaurant = (event) => {
    if (event?.target) {
      setRestaurant(event.target.value);
    }
  };

  const updateRestaurant = (id) => {
    setRestaurant(id);
  };

  const checkAndSetRestaurant = useCallback(
    (previousRestaurant) => {
      if (restaurantList.length > 0) {
        if (previousRestaurant === undefined) {
          setRestaurant(restaurantList[0].id);
        } else {
          const foundRestaurant = restaurantList.find((r) => Number(r.id) === Number(previousRestaurant));
          setRestaurant(foundRestaurant ? foundRestaurant.id : restaurantList[0].id);
        }
      }
    },
    [restaurantList]
  );

  useEffect(() => {
    if (restaurantList.length > 0) {
      const previousRestaurant = sessionStorage.getItem('restaurant_id');
      checkAndSetRestaurant(previousRestaurant ?? undefined);
    }
  }, [checkAndSetRestaurant, restaurantList]);

  useEffect(() => {
    if (restaurant !== undefined) {
      sessionStorage.setItem('restaurant_id', restaurant);
    }
  }, [restaurant]);

  return (
    <RestaurantContext.Provider
      value={{
        restaurant,
        restaurantList,
        setRestaurant,
        isLoading,
        updateRestaurant,
        onChangeRestaurant,
      }}
    >
      {children}
    </RestaurantContext.Provider>
  );
}

RestaurantProvider.propTypes = { children: PropTypes.node };

export { RestaurantProvider, RestaurantContext };
